import Box from '@mui/material/Box';
import React from 'react';
import * as yup from 'yup';

import { Form } from '@/components/molecules/Form';
import { FormFieldInput } from '@/components/molecules/FormFieldInput';
import { Field, FieldType } from '@/types/fields';
import { toEmptyStringIfNull } from '@/utility/toEmptyStringIfNull';
import { validationErrorToObject } from '@/utility/yup';

interface Props<T> {
  readonly name?: string;
  readonly fields: ReadonlyArray<Field<T>>;

  readonly validationSchema: yup.ObjectSchema<any, yup.AnyObject, any, any>;
  readonly value: T | null;

  readonly loading?: boolean;
}

export function DynamicForm<T>(props: Props<T>): React.ReactElement {
  const { name, fields, validationSchema, value, loading } = props;

  const errors = React.useMemo((): Record<string, yup.ValidationError> => {
    try {
      validationSchema.validateSync(value, {
        abortEarly: false,
      });

      return {};
    } catch (error: unknown) {
      if (error instanceof yup.ValidationError) {
        return validationErrorToObject(error);
      } else {
        return {};
      }
    }
  }, [validationSchema, value]);

  return (
    <Form name={name} validationSchema={validationSchema} loading={loading}>
      <Box display="flex" flexDirection="column" gap={5}>
        {fields.map((field: Field<T>): React.ReactElement => {
          // TODO: fix this mess with the types
          const key = field.name as keyof T;
          const fieldValue = toEmptyStringIfNull(
            field.type !== FieldType.embedded ? value?.[key] : value,
          );
          const error = field.displayErrors ? errors[String(key)] : undefined;

          return (
            <FormFieldInput
              key={String(key)}
              field={field}
              defaultValue={fieldValue}
              error={error}
              errors={errors}
            />
          );
        })}
      </Box>
    </Form>
  );
}
